import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';

import { ApiService } from '../../core/services/api.service';
import { ToastService } from '../../core/services/toast.service';

interface AssetSubCategory {
  id: string;
  name: string;
  sortOrder: number;
  isDefault: boolean;
  requiresDeviceDetails: boolean;
  categoryId: string;
}

interface AssetCategory {
  id: string;
  name: string;
  sortOrder: number;
  isDefault: boolean;
  subCategories: AssetSubCategory[];
}

interface AssetRow {
  id: string;
  name: string;
  assetTag?: string | null;
  category: string;
  categoryId?: string | null;
  subCategory?: string | null;
  subCategoryId?: string | null;
  serialNumber?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  systemConfiguration?: string | null;
  imeiNumber?: string | null;
  status: number;
  condition: number;
  location?: string | null;
  purchaseDate?: string | null;
  purchasePrice?: number | null;
  warrantyExpiry?: string | null;
  notes?: string | null;
}

const STATUS_LABELS = ['Available', 'Assigned', 'In Maintenance', 'Retired', 'Lost', 'Disposed'];
const CONDITION_LABELS = ['New', 'Good', 'Fair', 'Poor', 'Damaged'];

interface AssetIssueSummary {
  issuedCount: number;
  replacementCount: number;
  faultyCount: number;
  activeAssignments: number;
}

@Component({
  selector: 'app-assets',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule
  ],
  templateUrl: './assets.component.html',
  styleUrl: './assets.component.scss'
})
export class AssetsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);
  readonly assets = signal<AssetRow[]>([]);
  readonly categories = signal<AssetCategory[]>([]);
  readonly issueSummary = signal<AssetIssueSummary | null>(null);

  readonly selectedCategory = computed(() =>
    this.categories().find((c) => c.id === this.formCategoryId) ?? null
  );

  readonly selectedSubCategory = computed(() =>
    this.selectedCategory()?.subCategories.find((s) => s.id === this.formSubCategoryId) ?? null
  );

  readonly showDeviceFields = computed(() => !!this.selectedSubCategory()?.requiresDeviceDetails);

  readonly filteredSubCategories = computed(() => {
    const cat = this.categories().find((c) => c.id === this.filterCategoryId);
    return cat?.subCategories ?? [];
  });

  assetColumns = [
    'name',
    'category',
    'subCategory',
    'tag',
    'imei',
    'purchasePrice',
    'status',
    'device',
    'actions'
  ];

  // Filters
  filterCategoryId = '';
  filterSubCategoryId = '';
  search = '';

  // Asset form
  formName = '';
  formAssetTag = '';
  formCategoryId = '';
  formSubCategoryId = '';
  formSerialNumber = '';
  formManufacturer = '';
  formModel = '';
  formSystemConfiguration = '';
  formImeiNumber = '';
  formPurchaseDate = '';
  formPurchasePrice: number | null = null;
  formWarrantyExpiry = '';
  formLocation = '';
  formNotes = '';
  formStatus = 0;
  formCondition = 1;
  editingId: string | null = null;

  // Issue / replacement / faulty tracking (AssetAssignment)
  issueEmployeeId = '';
  issueDate = new Date().toISOString().slice(0, 10);
  issueType: 0 | 1 | 2 = 0; // 0 Issued, 1 Replacement, 2 Faulty
  issueNotes = '';

  // Category management
  newCategoryName = '';
  manageCategoryId = '';
  newSubCategoryName = '';

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.loadCategories();
    this.loadAssets();
    this.loadIssueSummary();
  }

  loadCategories(): void {
    this.api.get<AssetCategory[]>('v1/asset-categories').subscribe({
      next: (items) => {
        const list = items ?? [];
        this.categories.set(list);
        if (!this.formCategoryId && list.length) {
          this.formCategoryId = list[0].id;
          this.onCategoryChanged();
        }
        if (!this.manageCategoryId && list.length) {
          this.manageCategoryId = list[0].id;
        }
      },
      error: (err) => {
        const msg = err?.message || 'Failed to load asset categories.';
        this.error.set(msg);
        this.toast.error(msg);
      }
    });
  }

  loadAssets(): void {
    const params: Record<string, string | number> = { page: 1, pageSize: 100 };
    if (this.search.trim()) params['search'] = this.search.trim();

    const cat = this.categories().find((c) => c.id === this.filterCategoryId);
    if (cat) params['category'] = cat.name;
    const sub = cat?.subCategories.find((s) => s.id === this.filterSubCategoryId);
    if (sub) params['subCategory'] = sub.name;

    this.api.getPaged<AssetRow>('v1/assets', params).subscribe({
      next: (page) => {
        this.assets.set(page.items ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        const msg = err?.message || 'Failed to load assets.';
        this.error.set(msg);
        this.toast.error(msg);
      }
    });
  }

  onCategoryChanged(): void {
    const cat = this.categories().find((c) => c.id === this.formCategoryId);
    const currentSubOk = !!cat?.subCategories.some((s) => s.id === this.formSubCategoryId);
    const desiredSubId = currentSubOk ? this.formSubCategoryId : (cat?.subCategories[0]?.id ?? '');
    this.formSubCategoryId = desiredSubId;

    // Clear device fields when the chosen subcategory does not require device details.
    const requiresDevice = !!cat?.subCategories.find((s) => s.id === this.formSubCategoryId)?.requiresDeviceDetails;
    if (!requiresDevice) {
      this.formSystemConfiguration = '';
      this.formImeiNumber = '';
    }
  }

  onFilterCategoryChanged(): void {
    this.filterSubCategoryId = '';
    this.loadAssets();
  }

  loadIssueSummary(): void {
    this.api.get<AssetIssueSummary>('v1/assets/issue-summary').subscribe({
      next: (s) => this.issueSummary.set(s ?? null),
      error: () => {
        // Do not block assets UI if summary endpoint is not accessible for the current role.
      }
    });
  }

  issueAsset(): void {
    if (!this.editingId) {
      this.toast.warning('Select an asset (click Edit) before issuing it.');
      return;
    }

    const emp = this.issueEmployeeId.trim();
    const isGuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(emp);
    if (!isGuid) {
      this.toast.warning('Please enter a valid EmployeeId (GUID) in “Issued to”.');
      return;
    }

    const body = {
      employeeId: emp,
      assignedDate: this.issueDate,
      issueType: this.issueType,
      notes: this.issueNotes || null
    };

    this.api.post(`v1/assets/${this.editingId}/assignments`, body).subscribe({
      next: () => {
        this.issueEmployeeId = '';
        this.issueNotes = '';
        this.issueType = 0;
        this.reload();
      }
    });
  }

  saveAsset(): void {
    if (!this.formName.trim()) {
      this.toast.warning('Asset name is required.');
      return;
    }
    if (!this.formCategoryId || !this.formSubCategoryId) {
      this.toast.warning('Category and subcategory are required.');
      return;
    }

    const body = {
      name: this.formName.trim(),
      assetTag: this.formAssetTag || null,
      categoryId: this.formCategoryId,
      subCategoryId: this.formSubCategoryId,
      serialNumber: this.formSerialNumber || null,
      manufacturer: this.formManufacturer || null,
      model: this.formModel || null,
      systemConfiguration: this.showDeviceFields() ? this.formSystemConfiguration || null : null,
      imeiNumber: this.showDeviceFields() ? this.formImeiNumber || null : null,
      purchaseDate: this.formPurchaseDate || null,
      purchasePrice: this.formPurchasePrice,
      warrantyExpiry: this.formWarrantyExpiry || null,
      location: this.formLocation || null,
      notes: this.formNotes || null,
      status: this.formStatus,
      condition: this.formCondition
    };

    const req$ = this.editingId
      ? this.api.put(`v1/assets/${this.editingId}`, body)
      : this.api.post('v1/assets', body);

    req$.subscribe({
      next: () => {
        this.resetForm();
        this.reload();
      }
    });
  }

  editAsset(row: AssetRow): void {
    this.editingId = row.id;
    this.formName = row.name;
    this.formAssetTag = row.assetTag || '';
    this.formCategoryId = row.categoryId || this.categories().find((c) => c.name === row.category)?.id || '';
    this.onCategoryChanged();
    this.formSubCategoryId =
      row.subCategoryId ||
      this.selectedCategory()?.subCategories.find((s) => s.name === row.subCategory)?.id ||
      '';
    this.formSerialNumber = row.serialNumber || '';
    this.formManufacturer = row.manufacturer || '';
    this.formModel = row.model || '';
    this.formSystemConfiguration = row.systemConfiguration || '';
    this.formImeiNumber = row.imeiNumber || '';
    this.formPurchaseDate = row.purchaseDate || '';
    this.formPurchasePrice = row.purchasePrice ?? null;
    this.formWarrantyExpiry = row.warrantyExpiry || '';
    this.formLocation = row.location || '';
    this.formNotes = row.notes || '';
    this.formStatus = row.status ?? 0;
    this.formCondition = row.condition ?? 1;
    this.toast.info(`Editing "${row.name}".`);
  }

  deleteAsset(row: AssetRow): void {
    this.api.delete(`v1/assets/${row.id}`).subscribe({
      next: () => this.reload()
    });
  }

  resetForm(): void {
    this.editingId = null;
    this.formName = '';
    this.formAssetTag = '';
    this.formSerialNumber = '';
    this.formManufacturer = '';
    this.formModel = '';
    this.formSystemConfiguration = '';
    this.formImeiNumber = '';
    this.formPurchaseDate = '';
    this.formPurchasePrice = null;
    this.formWarrantyExpiry = '';
    this.formLocation = '';
    this.formNotes = '';
    this.formStatus = 0;
    this.formCondition = 1;
    if (this.categories().length) {
      this.formCategoryId = this.categories()[0].id;
      this.onCategoryChanged();
    }
  }

  createCategory(): void {
    const name = this.newCategoryName.trim();
    if (!name) {
      this.toast.warning('Category name is required.');
      return;
    }
    this.api.post('v1/asset-categories', { name }).subscribe({
      next: () => {
        this.newCategoryName = '';
        this.loadCategories();
      }
    });
  }

  deleteCategory(cat: AssetCategory): void {
    this.api.delete(`v1/asset-categories/${cat.id}`).subscribe({
      next: () => this.loadCategories()
    });
  }

  createSubCategory(): void {
    if (!this.manageCategoryId) {
      this.toast.warning('Select a category first.');
      return;
    }
    const name = this.newSubCategoryName.trim();
    if (!name) {
      this.toast.warning('Subcategory name is required.');
      return;
    }
    this.api.post(`v1/asset-categories/${this.manageCategoryId}/subcategories`, { name }).subscribe({
      next: () => {
        this.newSubCategoryName = '';
        this.loadCategories();
      }
    });
  }

  deleteSubCategory(sub: AssetSubCategory): void {
    this.api.delete(`v1/asset-categories/${sub.categoryId}/subcategories/${sub.id}`).subscribe({
      next: () => this.loadCategories()
    });
  }

  managedSubs(): AssetSubCategory[] {
    return this.categories().find((c) => c.id === this.manageCategoryId)?.subCategories ?? [];
  }

  statusLabel(status: number): string {
    return STATUS_LABELS[status] ?? String(status);
  }

  conditionLabel(condition: number): string {
    return CONDITION_LABELS[condition] ?? String(condition);
  }

  money(amount?: number | null): string {
    if (amount == null) return '—';
    return `USD ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}
